import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoSite from '../assets/logo-site.png';
import { authService, UserProfile } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';

interface LayoutProps {
  children: React.ReactNode;
  pageTitle: string;
  breadcrumb?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, pageTitle, breadcrumb = 'Visão Geral' }) => {
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const { signOut } = useAuth();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    authService.getProfile()
      .then(setProfile)
      .catch(() => console.error('Erro ao buscar perfil no layout'));
  }, []);

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: 'dashboard' },
    { path: '/recebimentos', label: 'Recebimentos', icon: 'receipt_long' },
    { path: '/contas-a-pagar', label: 'Contas a Pagar', icon: 'payments' },
    { path: '/dre', label: 'DRE', icon: 'analytics' },
    { path: '/configuracoes', label: 'Configurações', icon: 'settings' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Função para pegar iniciais
  const getInitials = (nome?: string) => {
    if (!nome) return '??';
    return nome.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 flex items-center justify-center border-b border-slate-800/80 mb-2">
        <img src={logoSite} alt="Chalk Finance Logo" className="h-8 object-contain" />
      </div>

      <nav className="flex-1 mt-6 px-4 space-y-2">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setIsMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${isActive(item.path)
              ? 'bg-slate-800/80 text-white border border-slate-700/50 shadow-lg shadow-black/20 scale-[1.02]'
              : 'text-slate-500 hover:bg-slate-800/40 hover:text-white hover:scale-[1.02] border border-transparent'
              }`}
          >
            <span className={`material-symbols-outlined text-[20px] ${isActive(item.path) ? 'text-primary' : ''}`}>{item.icon}</span>
            <span className={`text-sm ${isActive(item.path) ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800/80 flex flex-col gap-2">
        <Link 
          to="/configuracoes?tab=seguranca" 
          onClick={() => setIsMobileMenuOpen(false)}
          className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-800 hover:bg-slate-800/80 hover:border-slate-700/50 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center overflow-hidden text-slate-300 font-bold text-sm ring-2 ring-transparent group-hover:ring-primary/50 transition-all shadow-md">
            {profile?.avatar ? (
              <img src={profile.avatar} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              getInitials(profile?.nome)
            )}
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-xs font-bold truncate group-hover:text-white transition-colors text-slate-300">
              {profile?.nome || 'Carregando...'}
            </p>
            <p className="text-[10px] text-slate-500 truncate">{profile?.role || 'Usuário'}</p>
          </div>
          <span className="material-symbols-outlined text-slate-600 text-[16px] group-hover:text-primary transition-colors">settings</span>
        </Link>

        <button
          onClick={() => { signOut(); setIsMobileMenuOpen(false); }}
          className="flex items-center gap-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 hover:border-rose-500/30 transition-all cursor-pointer group text-rose-400 hover:text-rose-300 w-full text-left"
        >
          <div className="w-10 flex items-center justify-center">
            <span className="material-symbols-outlined text-[20px]">logout</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-xs font-bold truncate transition-colors">
              Sair da Conta
            </p>
          </div>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen overflow-hidden bg-background-dark text-slate-300">
      {/* Sidebar Desktop */}
      <aside className="w-64 flex-shrink-0 border-r border-slate-800/80 bg-background-dark/95 backdrop-blur-xl hidden md:flex flex-col z-20">
        <SidebarContent />
      </aside>

      {/* Mobile Drawer Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[40] md:hidden animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <aside 
        className={`fixed inset-y-0 left-0 w-72 bg-background-dark border-r border-slate-800 z-[50] md:hidden transform transition-transform duration-300 ease-in-out flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Main */}
      <main className="flex-1 h-screen overflow-y-auto bg-transparent relative">

        {/* Subtle Ambient Glow */}
        <div className="absolute top-0 left-0 right-0 h-96 bg-slate-800/20 blur-[120px] pointer-events-none -z-10" />

        {/* Header */}
        <header className="h-16 border-b border-slate-800/80 flex items-center px-4 md:px-8 bg-background-dark/80 backdrop-blur-xl sticky top-0 z-30 transition-all">
          <div className="flex items-center gap-4 w-full">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-all"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            
            <div className="flex items-center gap-2 text-xs md:text-sm overflow-hidden flex-1">
              <span className="text-slate-500 font-medium truncate hidden sm:inline">{breadcrumb}</span>
              <span className="material-symbols-outlined text-slate-600 text-[16px] hidden sm:inline">chevron_right</span>
              <span className="font-bold text-slate-200 truncate">{pageTitle}</span>
            </div>

            <ThemeToggle />

            <div className="md:hidden">
               <img src={logoSite} alt="Chalk Logo" className="h-6 object-contain" />
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 animate-fade-in-up">
          {children}
        </div>

        <div className="h-20" />
      </main>
    </div>
  );
};
